import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, FadeIn, Sheet, FullScreen, RefreshButton, useIsDesktop } from '../components/ui';
import NotificationBell from '../components/NotificationBell';
import BannerCarousel from '../components/BannerCarousel';
import StaffProfile from './StaffProfile';
import { categories as demoCategories } from '../api/demo';
import { TINTS } from '../theme';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

export default function Home({ onBook }) {
  const { user, selectedLocation, setSelectedLocation } = useAuth();
  const isDesktop = useIsDesktop();
  const isStaff = user?.role === 'staff';

  // Only real (API) staff — never demo — so every "View profile" actually loads.
  const [topStaff, setTopStaff] = useState([]);
  const [cats, setCats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showLocPicker, setShowLocPicker] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(() => Promise.allSettled([
    api.get('/staff/top-rated').then((d) => setTopStaff(Array.isArray(d?.staff) ? d.staff : [])),
    api.get('/catalog/categories').then((d) => { if (d?.categories?.length) setCats(d.categories); }),
    api.get('/catalog/locations').then((d) => {
      if (d?.locations?.length) {
        setLocations(d.locations);
        setSelectedLocation((cur) => cur || d.locations[0]);
      }
    }),
  ]).finally(() => setRefreshing(false)), [setSelectedLocation]);

  useEffect(() => { load(); }, [load]);
  const onRefresh = () => { setRefreshing(true); load(); };

  // Categories: admin catalog if available, else demo.
  const services = cats
    ? cats.map((c, i) => ({ key: c._id, label: c.name, icon: c.icon || '🧹', tint: TINTS[i % TINTS.length] }))
    : demoCategories;

  const q = query.trim().toLowerCase();
  const shown = q ? services.filter((s) => s.label.toLowerCase().includes(q)) : services;

  return (
    <div className="screen">
      <div className="header-red">
        <div className="row-between">
          {isStaff ? (
            <div style={{ fontWeight: 800, fontSize: 16 }}>Narayan</div>
          ) : (
            <button className="grow" style={{ textAlign: 'left', color: '#fff' }} onClick={() => setShowLocPicker(true)}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'rgba(255,255,255,0.75)' }}>📍 Location</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>
                {selectedLocation
                  ? [selectedLocation.name, selectedLocation.city].filter(Boolean).join(', ')
                  : 'Select location'} ▾
              </div>
            </button>
          )}
          <div className="row" style={{ gap: 8 }}>
            <RefreshButton onClick={onRefresh} busy={refreshing} />
            <NotificationBell />
          </div>
        </div>

        <FadeIn delay={80}>
          <h1 className="header-red__greeting">Namaste 🙏</h1>
          <p className="header-red__sub">
            {isStaff ? 'Here are your jobs & earnings' : 'What help do you need today?'}
          </p>
        </FadeIn>
      </div>

      {/* Search pill — overlaps the curved header, as in the app */}
      <FadeIn delay={140} style={{ margin: isDesktop ? '18px 0 0' : '-22px 16px 0' }}>
        <div className="row" style={{
          background: '#fff', borderRadius: 'var(--r-pill)',
          padding: '13px 18px', boxShadow: 'var(--shadow-card)',
        }}>
          <span style={{ fontSize: 16 }}>🔍</span>
          <input
            className="grow"
            style={{ border: 'none', outline: 'none', fontSize: 15, background: 'transparent' }}
            placeholder="Search maid, cook, babysitter…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search services"
          />
          {q ? <button className="faint" onClick={() => setQuery('')} aria-label="Clear search">✕</button> : null}
        </div>
      </FadeIn>

      <h2 className="section-title">Services</h2>
      {shown.length === 0 ? (
        <p className="muted center" style={{ padding: '0 16px 10px' }}>No service matches “{query}”.</p>
      ) : (
        <div className="cat-grid">
          {shown.map((c, i) => (
            <FadeIn key={c.key} delay={160 + i * 50}>
              <button
                onClick={() => onBook && onBook(c.label)}
                style={{ width: '100%', padding: '10px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <span style={{
                  width: 62, height: 62, borderRadius: 'var(--r-lg)', background: c.tint,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
                }}>{c.icon}</span>
                <span style={{ fontSize: 12, marginTop: 8, fontWeight: 600, textAlign: 'center' }}>{c.label}</span>
              </button>
            </FadeIn>
          ))}
        </div>
      )}

      <BannerCarousel
        audience={isStaff ? 'staff' : 'customer'}
        onSelect={(nav) => nav === 'packages' && onBook && onBook()}
      />

      <FadeIn delay={300}>
        <div className="row" style={{
          background: '#2B0A0D', margin: '8px 16px 0', borderRadius: 'var(--r-lg)', padding: 18, gap: 12,
        }}>
          <div className="grow">
            <div style={{ color: '#fff', fontSize: 17, fontWeight: 800 }}>100% Verified Staff</div>
            <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 12.5, marginTop: 4, lineHeight: 1.45 }}>
              Every maid is Aadhaar &amp; police verified before they serve you.
            </div>
          </div>
          <span style={{ fontSize: 44 }}>🛡️</span>
        </div>
      </FadeIn>

      {topStaff.length > 0 && (
        <>
          <h2 className="section-title">Top rated near you</h2>
          <div className={isDesktop ? 'staff-grid' : 'hscroll'}>
            {topStaff.map((s, i) => (
              <FadeIn key={s._id} delay={340 + i * 70}>
                <Card style={{ width: isDesktop ? '100%' : 200 }} onClick={() => setViewStaff(s._id)}>
                  <div className="row">
                    <span className="avatar" style={{ width: 40, height: 40 }}>
                      {s.profilePhoto ? <img src={s.profilePhoto} alt="" /> : (s.name || '?').charAt(0)}
                    </span>
                    <div className="grow">
                      <div className="row" style={{ gap: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                        <span style={{ fontSize: 12 }}>✅</span>
                      </div>
                      <div className="muted" style={{ fontSize: 12 }}>⭐ {s.rating || '—'} · {s.experienceYears || 0}y</div>
                    </div>
                  </div>
                  <div className="row-between" style={{ marginTop: 10 }}>
                    <Badge label={(s.skills && s.skills[0]) || 'Home Help'} tone="brand"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }} />
                    <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, whiteSpace: 'nowrap', flex: 'none' }}>View profile ›</span>
                  </div>
                </Card>
              </FadeIn>
            ))}
          </div>
        </>
      )}

      <FullScreen open={!!viewStaff} onClose={() => setViewStaff(null)}>
        {viewStaff && <StaffProfile staffId={viewStaff} onClose={() => setViewStaff(null)} />}
      </FullScreen>

      <Sheet open={showLocPicker} onClose={() => setShowLocPicker(false)} title="Select your location">
        {locations.length === 0 ? (
          <p className="muted center" style={{ padding: '20px 0' }}>No service locations added yet.</p>
        ) : (
          locations.map((l) => {
            const active = selectedLocation?._id === l._id;
            return (
              <button
                key={l._id}
                onClick={() => { setSelectedLocation(l); setShowLocPicker(false); }}
                className="row"
                style={{
                  width: '100%', textAlign: 'left', padding: '14px 12px',
                  borderRadius: 'var(--r-md)', background: active ? 'var(--primary-soft)' : 'transparent',
                }}
              >
                <span style={{ fontSize: 16 }}>📍</span>
                <span className="grow" style={{ fontWeight: 600 }}>
                  {[l.name, l.city].filter(Boolean).join(', ')} {l.pincode ? `· ${l.pincode}` : ''}
                </span>
                {active && <span style={{ color: 'var(--primary)', fontWeight: 800 }}>✓</span>}
              </button>
            );
          })
        )}
      </Sheet>
    </div>
  );
}
